import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Loader2, Trash2, ArrowUp, ArrowDown, Save, X, Search,
  AlertTriangle, PlayCircle, Undo2, Archive, RotateCcw, Sparkles, Eye,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authFetch = (path, opts = {}) => {
  const token = localStorage.getItem('rbr_token');
  return fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
};

const STATUS_TONE = {
  draft:     'bg-amber-50   text-amber-700  border-amber-200',
  published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  archived:  'bg-stone-100  text-stone-500  border-stone-200',
};

const inputCls = 'w-full px-3 py-2.5 rounded-xl bg-white border border-stone-200 focus:border-[#C79A4A] focus:outline-none text-sm';

// --- Content picker for adding sessions -------------------------------------
const SessionContentPicker = ({ onPick }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('status', 'published');
      params.set('pageSize', '25');
      if (q) params.set('q', q);
      const r = await authFetch(`/api/admin/content?${params.toString()}`);
      const d = await r.json();
      setItems(d.items || []);
    } finally { setLoading(false); }
  }, [q]);
  useEffect(() => { search(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
               placeholder="Search published content…" className={inputCls} data-testid="program-picker-search" />
        <button onClick={search} className="px-3 py-2 rounded-lg text-sm border border-stone-200 hover:bg-white">Go</button>
      </div>
      <div className="max-h-60 overflow-y-auto divide-y divide-stone-100 border border-stone-200 rounded-lg bg-white">
        {loading ? (
          <p className="text-center py-4 text-[12px] text-stone-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-center py-4 text-[12px] text-stone-500">No results.</p>
        ) : items.map((c) => (
          <button key={c.id} onClick={() => onPick(c)} data-testid={`program-picker-pick-${c.id}`}
                  className="w-full text-left flex items-center gap-2 px-3 py-1.5 hover:bg-[#F6F1E7]">
            <span className="text-[10px] uppercase tracking-wider text-[#C79A4A] font-bold w-24 truncate">{c.type}</span>
            <span className="text-[12px] text-stone-800 flex-1 truncate">{c.title}</span>
            <Plus className="w-4 h-4 text-stone-400" />
          </button>
        ))}
      </div>
    </div>
  );
};

// --- Program editor ---------------------------------------------------------
const ProgramEditor = ({ programId, onClose, onSaved }) => {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draftMeta, setDraftMeta] = useState({ title: '', description: '', category: '', totalDays: '', accessLevel: 'free' });
  const [addingSession, setAddingSession] = useState(false);
  const [dayForNew, setDayForNew] = useState('');

  const load = useCallback(async () => {
    const r = await authFetch(`/api/admin/programs/${programId}/preview`);
    if (r.ok) {
      const d = await r.json();
      setDetail(d);
      setDraftMeta({
        title: d.title || '',
        description: d.description || '',
        category: d.category || '',
        totalDays: d.totalDays || '',
        accessLevel: d.accessLevel || 'free',
      });
    }
  }, [programId]);
  useEffect(() => { load(); }, [load]);

  const saveMeta = async () => {
    setBusy(true);
    try {
      const r = await authFetch(`/api/admin/programs/${programId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...draftMeta,
          totalDays: draftMeta.totalDays ? Number(draftMeta.totalDays) : null,
        }),
      });
      if (r.ok) { toast.success('Saved'); load(); onSaved && onSaved(); }
      else { const d = await r.json(); toast.error(d.detail || 'Save failed'); }
    } finally { setBusy(false); }
  };

  const addSession = async (content) => {
    setBusy(true);
    try {
      const nextDay = (detail?.sessions?.length || 0) + 1;
      const r = await authFetch(`/api/admin/programs/${programId}/sessions`, {
        method: 'POST',
        body: JSON.stringify({ contentId: content.id, dayNumber: dayForNew ? Number(dayForNew) : nextDay }),
      });
      if (r.ok) { toast.success(`Added: ${content.title}`); setAddingSession(false); setDayForNew(''); load(); }
      else { const d = await r.json(); toast.error(d.detail || 'Failed'); }
    } finally { setBusy(false); }
  };

  const move = async (idx, delta) => {
    const sessions = [...(detail?.sessions || [])];
    const j = idx + delta;
    if (j < 0 || j >= sessions.length) return;
    [sessions[idx], sessions[j]] = [sessions[j], sessions[idx]];
    const ids = sessions.map((s) => s.id);
    const r = await authFetch(`/api/admin/programs/${programId}/sessions/reorder`, {
      method: 'PATCH', body: JSON.stringify({ sessionIds: ids }),
    });
    if (r.ok) load();
    else { const d = await r.json(); toast.error(d.detail || 'Reorder failed'); }
  };

  const removeSession = async (sid) => {
    if (!window.confirm('Remove this session?')) return;
    const r = await authFetch(`/api/admin/programs/${programId}/sessions/${sid}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Removed'); load(); }
  };

  const lifecycle = async (action) => {
    let path = `/api/admin/programs/${programId}`;
    let method = 'POST';
    if (action === 'publish')   path += '/publish';
    if (action === 'unpublish') path += '/unpublish';
    if (action === 'restore')   path += '/restore';
    if (action === 'archive')   { method = 'DELETE'; }
    setBusy(true);
    try {
      const r = await authFetch(path, { method });
      const d = await r.json();
      if (!r.ok) {
        const errs = d?.detail?.errors || [];
        toast.error(errs.length ? `Cannot ${action}: ${errs.join(' · ')}` : (d.detail || 'Failed'));
        return;
      }
      toast.success(`${action}d`);
      load(); onSaved && onSaved();
    } finally { setBusy(false); }
  };

  if (!detail) return <p className="text-center py-8 text-stone-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</p>;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" data-testid="program-editor-modal" onClick={onClose}>
      <div className="bg-[#FFFCF6] w-full max-w-3xl rounded-2xl border border-stone-200 shadow-lg p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-xl text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>{detail.title || 'Program'}</h3>
            <span className={`inline-block mt-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${STATUS_TONE[detail.status] || 'bg-stone-50'}`}>{detail.status}</span>
          </div>
          <button onClick={onClose} className="p-1 text-stone-400"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <label className="text-[11px] uppercase tracking-wider text-stone-500 col-span-2">Title
            <input className={inputCls + ' mt-1'} value={draftMeta.title} onChange={(e) => setDraftMeta({...draftMeta, title: e.target.value})} data-testid="program-editor-title" />
          </label>
          <label className="text-[11px] uppercase tracking-wider text-stone-500 col-span-2">Description
            <textarea rows={3} className={inputCls + ' mt-1'} value={draftMeta.description} onChange={(e) => setDraftMeta({...draftMeta, description: e.target.value})} />
          </label>
          <label className="text-[11px] uppercase tracking-wider text-stone-500">Category
            <input className={inputCls + ' mt-1'} value={draftMeta.category} onChange={(e) => setDraftMeta({...draftMeta, category: e.target.value})} />
          </label>
          <label className="text-[11px] uppercase tracking-wider text-stone-500">Total days
            <input type="number" min="1" max="365" className={inputCls + ' mt-1'} value={draftMeta.totalDays} onChange={(e) => setDraftMeta({...draftMeta, totalDays: e.target.value})} />
          </label>
          <label className="text-[11px] uppercase tracking-wider text-stone-500 col-span-2">Access
            <select value={draftMeta.accessLevel} onChange={(e) => setDraftMeta({...draftMeta, accessLevel: e.target.value})} className={inputCls + ' mt-1'}>
              <option value="free">Free</option>
              <option value="premium">Premium</option>
            </select>
          </label>
        </div>

        <div className="mt-3 pt-3 border-t border-stone-200">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-stone-500">Sessions ({detail.sessions.length})</p>
            {(detail.warnings || []).length > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] text-amber-800"><AlertTriangle className="w-3 h-3" /> {detail.warnings.length} warning(s)</span>
            )}
            <button onClick={() => setAddingSession(true)} className="ml-auto text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-[#173F35] text-[#FFFCF6] hover:bg-[#0D2B24]" data-testid="program-add-session-btn">
              <Plus className="w-3 h-3 inline mr-1" /> Add session
            </button>
          </div>
          <ul className="space-y-1.5">
            {detail.sessions.map((s, idx) => {
              const c = s.content || {};
              return (
                <li key={s.id} data-testid={`program-session-${s.id}`} className="flex items-center gap-2 px-2.5 py-2 bg-white rounded-lg border border-stone-200">
                  <span className="text-[10px] font-mono w-8 text-stone-400">D{s.dayNumber}</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#C79A4A] font-bold w-24 truncate">{c.type}</span>
                  <span className="text-[12px] text-stone-800 flex-1 truncate">{c.title || <span className="text-rose-600 italic">missing content</span>}</span>
                  <button onClick={() => move(idx, -1)} data-testid={`program-move-up-${s.id}`} className="p-1 text-stone-400 hover:text-stone-700 disabled:opacity-30" disabled={idx === 0}><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => move(idx, +1)} data-testid={`program-move-down-${s.id}`} className="p-1 text-stone-400 hover:text-stone-700 disabled:opacity-30" disabled={idx === detail.sessions.length - 1}><ArrowDown className="w-4 h-4" /></button>
                  <button onClick={() => removeSession(s.id)} data-testid={`program-remove-${s.id}`} className="p-1 text-rose-500"><Trash2 className="w-4 h-4" /></button>
                </li>
              );
            })}
          </ul>
        </div>

        {addingSession && (
          <div className="mt-3 p-3 bg-[#F6F1E7] rounded-lg" data-testid="program-add-session-panel">
            <div className="flex items-center gap-2 mb-2">
              <label className="text-[10px] uppercase tracking-wider text-stone-500">Assign to day (optional):</label>
              <input type="number" min="1" max="365" className="w-20 px-2 py-1 rounded border border-stone-200 text-[12px]" value={dayForNew} onChange={(e) => setDayForNew(e.target.value)} />
              <button onClick={() => setAddingSession(false)} className="ml-auto text-[11px] text-stone-500 hover:text-stone-800">Cancel</button>
            </div>
            <SessionContentPicker onPick={addSession} />
          </div>
        )}

        {(detail.warnings || []).length > 0 && (
          <ul className="mt-3 space-y-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
            {detail.warnings.map((w) => (
              <li key={w.sessionId} className="flex items-start gap-1.5"><AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" /> {w.reason} {w.contentTitle ? `— ${w.contentTitle}` : ''}</li>
            ))}
          </ul>
        )}

        {(detail.publishValidationErrors || []).length > 0 && (
          <ul className="mt-3 space-y-1 text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded-lg p-3">
            <li className="font-semibold">Cannot publish yet:</li>
            {detail.publishValidationErrors.map((e, i) => (<li key={i}>· {e}</li>))}
          </ul>
        )}

        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-stone-200">
          <button onClick={saveMeta} disabled={busy} data-testid="program-save-meta" className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#173F35] text-[#FFFCF6] text-[13px] font-semibold hover:bg-[#0D2B24] disabled:opacity-60">
            <Save className="w-4 h-4" /> Save changes
          </button>
          {detail.status === 'draft' && (
            <button onClick={() => lifecycle('publish')} disabled={busy || !detail.canPublish} data-testid="program-publish" className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#C79A4A] text-[#0D2B24] text-[13px] font-semibold hover:bg-[#B8873A] disabled:opacity-60">
              <PlayCircle className="w-4 h-4" /> Publish
            </button>
          )}
          {detail.status === 'published' && (
            <button onClick={() => lifecycle('unpublish')} data-testid="program-unpublish" className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-stone-200 text-[13px] hover:bg-stone-50">
              <Undo2 className="w-4 h-4" /> Unpublish
            </button>
          )}
          {detail.status === 'archived' ? (
            <button onClick={() => lifecycle('restore')} data-testid="program-restore" className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-emerald-200 text-emerald-700 text-[13px] hover:bg-emerald-50">
              <RotateCcw className="w-4 h-4" /> Restore
            </button>
          ) : (
            <button onClick={() => lifecycle('archive')} data-testid="program-archive" className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-rose-200 text-rose-600 text-[13px] hover:bg-rose-50">
              <Archive className="w-4 h-4" /> Archive
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Segment Preview panel --------------------------------------------------
const SegmentPreviewPanel = () => {
  const [seg, setSeg] = useState({ mood: '', daypart: '', onboardingReasons: '', preferredContentTypes: '', activeProgramId: '' });
  const [items, setItems] = useState([]);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const body = {
        mood: seg.mood || null,
        daypart: seg.daypart || null,
        onboardingReasons: seg.onboardingReasons ? seg.onboardingReasons.split(',').map(s => s.trim()).filter(Boolean) : [],
        preferredContentTypes: seg.preferredContentTypes ? seg.preferredContentTypes.split(',').map(s => s.trim()).filter(Boolean) : [],
        activeProgramId: seg.activeProgramId || null,
        limit: 12,
      };
      const r = await authFetch('/api/admin/recommend/preview', { method: 'POST', body: JSON.stringify(body) });
      const d = await r.json();
      if (r.ok) { setItems(d.items || []); setNotice(d.notice || ''); }
      else toast.error(d.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#FFFCF6] rounded-2xl border border-stone-200 p-4" data-testid="segment-preview-panel">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-[#C79A4A]" />
        <h4 className="text-sm font-semibold text-stone-800">Segment preview</h4>
        <span className="text-[11px] text-stone-500 italic ml-auto">Simulated — never uses real user data</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
        <select value={seg.mood} onChange={(e) => setSeg({...seg, mood: e.target.value})} className={inputCls} data-testid="segment-mood">
          <option value="">Any mood</option>
          <option value="overwhelmed">Overwhelmed</option>
          <option value="anxious">Anxious</option>
          <option value="peaceful">Peaceful</option>
          <option value="grateful">Grateful</option>
          <option value="tired">Tired</option>
        </select>
        <select value={seg.daypart} onChange={(e) => setSeg({...seg, daypart: e.target.value})} className={inputCls} data-testid="segment-daypart">
          <option value="">Any daypart</option>
          <option value="morning">Morning</option>
          <option value="afternoon">Afternoon</option>
          <option value="evening">Evening</option>
          <option value="night">Night</option>
        </select>
        <input placeholder="Onboarding reasons (comma sep)" value={seg.onboardingReasons} onChange={(e) => setSeg({...seg, onboardingReasons: e.target.value})} className={inputCls} data-testid="segment-onboarding" />
        <input placeholder="Content types (comma sep)" value={seg.preferredContentTypes} onChange={(e) => setSeg({...seg, preferredContentTypes: e.target.value})} className={inputCls} data-testid="segment-types" />
        <input placeholder="Active program id (optional)" value={seg.activeProgramId} onChange={(e) => setSeg({...seg, activeProgramId: e.target.value})} className={inputCls} data-testid="segment-program" />
        <button onClick={run} disabled={busy} data-testid="segment-run" className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#173F35] text-[#FFFCF6] text-sm hover:bg-[#0D2B24] disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          Preview
        </button>
      </div>
      {notice && <p className="text-[11px] text-stone-500 italic mb-2">{notice}</p>}
      {items.length > 0 && (
        <ul className="divide-y divide-stone-100">
          {items.map((x) => (
            <li key={x.id} data-testid={`segment-item-${x.id}`} className="flex items-center gap-2 py-1.5">
              <span className="text-[10px] uppercase tracking-wider text-[#C79A4A] font-bold w-24 truncate">{x.type}</span>
              <span className="text-[12px] text-stone-800 flex-1 truncate">{x.title}</span>
              {x.inActiveProgram && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">In program</span>}
              <span className="text-[10px] text-stone-500">{x.reason}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// --- Root tab ---------------------------------------------------------------
const ProgramsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ q: '', status: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState(null);
  const [newTitle, setNewTitle] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('page', String(page));
      params.set('pageSize', '20');
      const r = await authFetch(`/api/admin/programs?${params.toString()}`);
      const d = await r.json();
      if (r.ok) { setRows(d.items || []); setTotalPages(d.totalPages || 1); setTotal(d.total || 0); }
    } finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const createProgram = async () => {
    if (!newTitle.trim()) return toast.error('Give it a title');
    const r = await authFetch('/api/admin/programs', {
      method: 'POST',
      body: JSON.stringify({ title: newTitle }),
    });
    const d = await r.json();
    if (r.ok) { toast.success('Draft program created'); setNewTitle(''); setEditing(d.id); load(); }
    else toast.error(d.detail || 'Failed');
  };

  return (
    <div data-testid="admin-programs-tab" className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute top-2.5 left-3 text-stone-400" />
          <input value={filters.q} onChange={(e) => setFilters(f => ({...f, q: e.target.value}))} onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
                 placeholder="Search programs…" className={'pl-9 ' + inputCls} data-testid="programs-search" />
        </div>
        <select value={filters.status} onChange={(e) => { setFilters(f => ({...f, status: e.target.value})); setPage(1); }} className={inputCls + ' max-w-[140px]'} data-testid="programs-status-filter">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
        <span className="ml-auto text-[12px] text-stone-500" data-testid="programs-total">{total} programs</span>
      </div>

      <div className="p-3 bg-[#FFFCF6] rounded-xl border border-stone-200 flex gap-2">
        <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') createProgram(); }}
               placeholder="New program title (e.g. 7 Days to Breathe Again)" className={inputCls + ' flex-1'} data-testid="programs-new-title" />
        <button onClick={createProgram} data-testid="programs-new-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#C79A4A] text-[#0D2B24] text-[13px] font-semibold hover:bg-[#B8873A]">
          <Plus className="w-4 h-4" /> Create draft
        </button>
      </div>

      {loading ? (
        <p className="text-center py-8 text-stone-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</p>
      ) : rows.length === 0 ? (
        <div className="text-center py-10 bg-[#FFFCF6] rounded-2xl border border-stone-200">
          <p className="text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>No programs yet.</p>
          <p className="text-[12px] text-stone-500 mt-1">Create your first program above — the classic 5 are ready to be built when Rowdy is.</p>
        </div>
      ) : (
        <ul className="divide-y divide-stone-100 bg-[#FFFCF6] rounded-xl border border-stone-200">
          {rows.map((p) => (
            <li key={p.id} data-testid={`programs-row-${p.id}`} className="flex items-center gap-3 px-3 py-2.5 hover:bg-[#F6F1E7]">
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-stone-800 truncate">{p.title}</p>
                <p className="text-[11px] text-stone-500">
                  {(p.sessionCount ?? (p.sessions?.length || 0))} session(s) · {p.totalDays || '—'} days · {p.accessLevel}
                </p>
              </div>
              <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${STATUS_TONE[p.status] || 'bg-stone-50'}`}>{p.status}</span>
              <button onClick={() => setEditing(p.id)} data-testid={`programs-open-${p.id}`} className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-stone-200 hover:bg-white">Open</button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between text-[12px] text-stone-500">
        <span>Page {page} of {totalPages}</span>
        <div className="flex gap-2">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="px-3 py-1 rounded-lg border border-stone-200 disabled:opacity-40">Prev</button>
          <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className="px-3 py-1 rounded-lg border border-stone-200 disabled:opacity-40">Next</button>
        </div>
      </div>

      <SegmentPreviewPanel />

      {editing && (
        <ProgramEditor programId={editing} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
};

export default ProgramsTab;
